import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { findNotificationDocIdByPushId, getNotificationById } from '../utils/notificationUtils';

/**
 * Handles navigation when push notifications are clicked.
 * Listens for NOTIFICATION_CLICKED messages from the service worker
 * and uses React Router to navigate (instead of full page reload).
 */
export const NotificationNavigationHandler: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleNotificationNavigation = async (event: MessageEvent | CustomEvent) => {
      try {
        const data = 'data' in event ? event.data : (event as CustomEvent).detail;

        if (data && data.type === 'NOTIFICATION_CLICKED') {
          let url = data.url || undefined;
          const pushId = data.notificationId || data.pushId || undefined;

          console.log('[NotificationNav] Handling notification click:', { url, pushId });

          // If no URL provided, try to resolve pushId -> docId
          if (!url && pushId) {
            try {
              const docId = await findNotificationDocIdByPushId(pushId, 5, 400);
              if (docId) {
                try {
                  const notif = await getNotificationById(docId);
                  const t = notif?.type;
                  const soId = notif?.data?.salesOrderId;
                  if ((t === 'sales_order_approver' || t === 'sales_order_created' || t === 'sales_order_updated') && soId) {
                    url = `/sales-order/${soId}`;
                    console.log('[NotificationNav] Resolved pushId to sales-order route:', soId);
                  } else {
                    url = `/notifications/${docId}`;
                    console.log('[NotificationNav] Resolved pushId to docId:', docId);
                  }
                } catch (err) {
                  url = `/notifications/${docId}`;
                  console.log('[NotificationNav] Resolved pushId to docId (fetch failed):', docId);
                }
              } else {
                url = `/notifications?openPushId=${encodeURIComponent(pushId)}`;
                console.log('[NotificationNav] Could not resolve pushId, falling back to list with openPushId');
              }
            } catch (err) {
              console.error('[NotificationNav] Resolver error:', err);
              url = `/notifications?openPushId=${encodeURIComponent(pushId)}`;
            }
          }

          const finalUrl = url || '/';

          // Extract the path from the URL (remove origin if present)
          let path = finalUrl;
          try {
            if (finalUrl.startsWith('http')) {
              const urlObj = new URL(finalUrl);
              path = urlObj.pathname + urlObj.search + urlObj.hash;
            }
          } catch (e) {
            path = finalUrl;
          }

          // Use React Router to navigate (no full page reload)
          console.log('[NotificationNav] Navigating to:', path);
          navigate(path);
        }
      } catch (error) {
        console.error('[NotificationNav] Error handling navigation:', error);
      }
    };

    // Listen for service worker messages
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('message', handleNotificationNavigation);
    }

    // Also listen for window messages
    window.addEventListener('message', handleNotificationNavigation);

    // Listen for custom events
    const customEventHandler = (event: Event) => {
      handleNotificationNavigation(event as CustomEvent);
    };
    window.addEventListener('notification-navigate', customEventHandler);

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleNotificationNavigation);
      }
      window.removeEventListener('message', handleNotificationNavigation);
      window.removeEventListener('notification-navigate', customEventHandler);
    };
  }, [navigate]);

  return null;
};

export default NotificationNavigationHandler;
